/**
 * src/components/spatial/SpatialEventComposer.tsx
 *
 * Putting on an event, without meeting a contract.
 *
 * The form is built from `eventFormModel`, which is built from the
 * split the server already draws between the fields that describe an
 * occasion and the fields that only matter once something is running
 * on-chain. So the basic form here is complete on its own: a name, a
 * time, a place inside the venue, who can come. Everything else is
 * behind one fold that says what it is for before it asks for
 * anything.
 *
 * It opens from the venue rather than from an admin screen, because
 * the person creating an event is standing in front of the building
 * they want to use. The existing admin overlay keeps its event fields
 * for operators editing raw objects; this is the same event, asked for
 * in the order a person would answer.
 *
 * The advanced fold is a courtesy and not a lock. The server
 * authorizes every field against the event's authority model, and
 * several of these additionally require the venue's agreement — marked
 * as such so somebody hears about it before filling in a form that was
 * always going to be refused, never as a substitute for the check.
 */

import React, { useMemo, useState } from 'react';

import { MapEntityTooltip } from '../map/MapEntityTooltip';
import {
  createEvent, editEvent, type EventRecord,
} from '../../services/spatial/spatialEventsClient';
import {
  ADVANCED_GROUPS,
  BASIC_GROUPS,
  draftUsesAdvanced,
  eventPatchFrom,
  validateEventForm,
  type EventFieldGroup,
  type EventFieldSpec,
} from './eventFormModel';

const CARD_WIDTH = 340;

export interface SpatialEventComposerProps {
  /** The venue the event is being created at. */
  hostObjectId: string;
  hostName?: string | null;
  /** Set when editing rather than creating. */
  eventObjectId?: string | null;
  initial?: Partial<EventRecord> | null;
  anchor: { x: number; y: number };
  viewport?: { width: number; height: number } | null;
  accentColor?: string;
  wallet?: string | null;
  onSaved?: (eventObjectId: string) => void;
  onClose: () => void;
}

const labelStyle: React.CSSProperties = {
  fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.6, color: '#64748b',
};
const noteStyle: React.CSSProperties = { fontSize: 11, color: '#94a3b8', lineHeight: 1.5 };
const inputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box',
  background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(148,163,184,0.2)',
  borderRadius: 6, padding: '5px 8px', fontSize: 12, color: '#e2e8f0',
};
const buttonStyle: React.CSSProperties = {
  border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 11,
  fontWeight: 700, background: 'rgba(99,102,241,0.3)', color: '#818cf8',
  cursor: 'pointer',
};
const quietButtonStyle: React.CSSProperties = {
  ...buttonStyle, background: 'rgba(148,163,184,0.14)', color: '#cbd5e1',
};

/** The input for one field. Kind drives the control, nothing else does. */
const Field: React.FC<{
  spec: EventFieldSpec;
  value: unknown;
  problem: string | null;
  onChange: (value: unknown) => void;
}> = ({ spec, value, problem, onChange }) => {
  const name = String(spec.name);
  const text = value == null ? '' : String(value);

  return (
    <label htmlFor={`event-field-${name}`} style={{ display: 'block' }}>
      <span style={{ ...labelStyle, display: 'block', marginBottom: 2 }}>
        {spec.label}
        {spec.required ? ' *' : ''}
        {spec.needsHostAuthority ? ' · needs the venue' : ''}
      </span>

      {spec.kind === 'textarea' ? (
        <textarea
          id={`event-field-${name}`}
          style={{ ...inputStyle, minHeight: 54, resize: 'vertical' }}
          value={text}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : spec.kind === 'select' ? (
        <select
          id={`event-field-${name}`}
          style={inputStyle}
          value={text}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">Choose…</option>
          {(spec.options || []).map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      ) : spec.kind === 'tags' ? (
        <input
          id={`event-field-${name}`}
          style={inputStyle}
          value={Array.isArray(value) ? value.join(', ') : text}
          placeholder="music, outdoors"
          onChange={(e) => onChange(
            e.target.value.split(',').map((t) => t.trim()).filter(Boolean),
          )}
        />
      ) : (
        <input
          id={`event-field-${name}`}
          style={inputStyle}
          type={spec.kind === 'datetime'
            ? 'datetime-local'
            : (spec.kind === 'number' ? 'number' : 'text')}
          value={spec.kind === 'datetime' ? text.slice(0, 16) : text}
          onChange={(e) => onChange(
            spec.kind === 'number'
              ? (e.target.value === '' ? '' : Number(e.target.value))
              : e.target.value,
          )}
        />
      )}

      {problem
        ? <span style={{ ...noteStyle, color: '#f87171' }}>{problem}</span>
        : (spec.hint ? <span style={noteStyle}>{spec.hint}</span> : null)}
    </label>
  );
};

const Group: React.FC<{
  group: EventFieldGroup;
  draft: Record<string, unknown>;
  problems: Record<string, string>;
  forceOpen?: boolean;
  onChange: (field: string, value: unknown) => void;
}> = ({ group, draft, problems, forceOpen = false, onChange }) => {
  const body = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 6 }}>
      <span style={noteStyle}>{group.blurb}</span>
      {group.fields.map((spec) => (
        <Field
          key={String(spec.name)}
          spec={spec}
          value={draft[String(spec.name)]}
          problem={problems[String(spec.name)] || null}
          onChange={(value) => onChange(String(spec.name), value)}
        />
      ))}
    </div>
  );

  if (group.openByDefault || forceOpen) {
    return (
      <section>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#e2e8f0' }}>
          {group.title}
        </span>
        {body}
      </section>
    );
  }

  return (
    <details open={forceOpen}>
      <summary style={{ fontSize: 12, fontWeight: 700, color: '#cbd5e1', cursor: 'pointer' }}>
        {group.title}
      </summary>
      {body}
    </details>
  );
};

export const SpatialEventComposer: React.FC<SpatialEventComposerProps> = ({
  hostObjectId,
  hostName = null,
  eventObjectId = null,
  initial = null,
  anchor,
  viewport = null,
  accentColor = '#a855f7',
  wallet = null,
  onSaved,
  onClose,
}) => {
  const [draft, setDraft] = useState<Record<string, unknown>>(
    () => ({ visibility: 'public', ...(initial || {}) }),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shown, setShown] = useState(false);

  // Opened for an event that already has machinery, so a contract
  // somebody attached last week is not hidden behind a fold they have
  // to remember to open.
  const advancedOpen = useMemo(
    () => draftUsesAdvanced(initial) || false,
    [initial],
  );

  const problems = useMemo(() => {
    if (!shown) return {} as Record<string, string>;
    const map: Record<string, string> = {};
    for (const problem of validateEventForm(draft as Partial<EventRecord>)) {
      if (problem.field) map[problem.field] = problem.message;
    }
    return map;
  }, [draft, shown]);

  const save = async () => {
    setShown(true);
    const found = validateEventForm(draft as Partial<EventRecord>);
    if (found.length > 0) return;

    setSaving(true);
    setError(null);
    const patch = eventPatchFrom(draft);
    const answer = eventObjectId
      ? await editEvent(eventObjectId, { wallet: wallet || undefined, event: patch })
      : await createEvent({
        hostObjectId,
        wallet: wallet || undefined,
        event: patch,
        name: String(draft.title || 'Event'),
      });
    setSaving(false);

    if (!answer.ok) {
      // The server's refusal, reported as the server's. It is the
      // authoritative one whatever this form decided to show.
      setError(answer.reason === 'not_authorized'
        ? 'You are not allowed to put an event on at this venue.'
        : (answer.reason === 'host_authority_required'
          ? 'Some of those settings need the venue to agree.'
          : `That did not save: ${answer.reason}`));
      return;
    }

    onSaved?.(eventObjectId || (answer as unknown as { eventId: string }).eventId);
  };

  return (
    <MapEntityTooltip
      data-testid="spatial-event-composer"
      anchor={anchor}
      viewport={viewport}
      surfaceId={`event-composer-${eventObjectId || hostObjectId}`}
      surfaceKind="event_panel"
      width={CARD_WIDTH}
      estimatedHeight={420}
      accentColor={accentColor}
      title={eventObjectId ? 'Edit event' : `Put on an event${hostName ? ` at ${hostName}` : ''}`}
      badge={(
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          style={{
            border: 'none', background: 'rgba(255,255,255,0.1)', color: '#94a3b8',
            borderRadius: 4, padding: '1px 6px', fontSize: 12, cursor: 'pointer',
          }}
        >
          ✕
        </button>
      )}
    >
      <div
        style={{
          padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 12,
          maxHeight: 'min(70vh, 560px)', overflowY: 'auto',
        }}
      >
        {BASIC_GROUPS.map((group) => (
          <Group
            key={group.id}
            group={group}
            draft={draft}
            problems={problems}
            onChange={(field, value) => setDraft((d) => ({ ...d, [field]: value }))}
          />
        ))}

        <details open={advancedOpen}>
          <summary style={{ fontSize: 12, fontWeight: 700, color: '#cbd5e1', cursor: 'pointer' }}>
            Advanced
          </summary>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 8 }}>
            <span style={noteStyle}>
              None of this is needed for an ordinary event.
            </span>
            {ADVANCED_GROUPS.map((group) => (
              <Group
                key={group.id}
                group={group}
                draft={draft}
                problems={problems}
                forceOpen
                onChange={(field, value) => setDraft((d) => ({ ...d, [field]: value }))}
              />
            ))}
          </div>
        </details>

        {error && <div style={{ ...noteStyle, color: '#f87171' }}>{error}</div>}

        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
          <button type="button" style={quietButtonStyle} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            style={buttonStyle}
            disabled={saving}
            onClick={() => void save()}
          >
            {saving ? 'Saving…' : (eventObjectId ? 'Save' : 'Create event')}
          </button>
        </div>
      </div>
    </MapEntityTooltip>
  );
};

export default SpatialEventComposer;
